import { modifyTargetPlanWeekdays, modifyTargetPlans } from '../api/workoutApi';

function weekdayLinkId(plan) {
  return plan?.id || plan?.target_plan_weekday_id || null;
}

/** Assign an existing plan to other weekdays in the same duration. */
export async function assignPlanToWeekdays({
  post,
  eventTypeId,
  durationId,
  sourcePlan,
  targetWeekdayIds = [],
  weekdayDetails = null,
}) {
  const planId = sourcePlan?.target_plan_id;
  if (!planId) return { success: false, message: 'Source plan is required.' };
  if (!durationId) return { success: false, message: 'Duration is required.' };
  if (!targetWeekdayIds.length) return { success: false, message: 'Select at least one day.' };

  const usage = {};
  Object.values(weekdayDetails || {}).forEach((day) => {
    (day.plans || []).forEach((p) => {
      if (p.target_plan_id) usage[p.target_plan_id] = (usage[p.target_plan_id] || 0) + 1;
    });
  });

  const deleteWeekdayOps = [];
  const orphanPlanIds = new Set();
  const addWeekdayOps = [];
  let assigned = 0;

  for (const weekdayId of [...new Set(targetWeekdayIds.map(String))]) {
    const day =
      weekdayDetails?.[weekdayId] ||
      Object.values(weekdayDetails || {}).find((d) => String(d.weekday_id) === String(weekdayId));
    const existing = day?.plans?.[0];

    if (existing?.target_plan_id && String(existing.target_plan_id) === String(planId)) continue;

    if (existing?.target_plan_id) {
      const linkId = weekdayLinkId(existing);
      if (!linkId) {
        return {
          success: false,
          message: `Could not clear the existing plan on ${day?.name || 'that day'}.`,
        };
      }
      deleteWeekdayOps.push({ operation: 'DELETE', id: linkId });
      usage[existing.target_plan_id] = (usage[existing.target_plan_id] || 1) - 1;
      if (usage[existing.target_plan_id] <= 0) orphanPlanIds.add(existing.target_plan_id);
    }

    addWeekdayOps.push({
      operation: 'ADD',
      id: null,
      target_plan_id: planId,
      target_plan_duration_id: durationId,
      weekday_id: day?.weekday_id ?? weekdayId,
    });
    assigned += 1;
  }

  if (!assigned) {
    return { success: true, message: 'This plan is already assigned to the selected day(s).' };
  }

  const weekdayResult = await modifyTargetPlanWeekdays(post, {
    event_type_id: eventTypeId,
    target_plan_weekdays: [...deleteWeekdayOps, ...addWeekdayOps],
  });
  if (!weekdayResult.success) {
    return {
      success: false,
      message: weekdayResult.message || 'Failed to assign plan to selected days.',
    };
  }

  if (orphanPlanIds.size > 0) {
    const deleteResult = await modifyTargetPlans(post, {
      event_type_id: eventTypeId,
      target_plans: [...orphanPlanIds].map((id) => ({ operation: 'DELETE', id })),
    });
    if (!deleteResult.success) {
      // Weekday links already updated — don't fail the whole action.
      return {
        success: true,
        warning: true,
        message:
          deleteResult.message ||
          `Assigned to ${assigned} day${assigned === 1 ? '' : 's'}, but cleanup of replaced plans failed.`,
      };
    }
  }

  return {
    success: true,
    message: `Assigned to ${assigned} day${assigned === 1 ? '' : 's'}.`,
  };
}
